import { logException } from '@lumapps/base-api/utils/logException';
import { parseJson } from '@lumapps/utils/object/parse';

import { DEFAULT_STREAM_EVENT_NAME, EXTRACT_STREAMING_FIELDS_REGEX, SSE_FRAME_DELIMITER } from './constants';
import type { StreamError, StreamEvent, StreamFormattedChunk, StreamingResponse } from './types';

/**
 * Check if the data returned by the stream are errors
 */
export function isStreamError<E extends StreamError>(stream: any): stream is StreamFormattedChunk<E> {
    return 'errors' in stream;
}

/**
 * Check if the data returned by the stream are data
 */
export function isStreamData<E extends StreamEvent>(stream: any): stream is StreamFormattedChunk<E> {
    return 'data' in stream;
}

/**
 * Splits `buffer` on `delimiter`, returning every complete segment found so far and the
 * trailing (possibly incomplete) segment to keep buffering until it's completed by later text.
 */
export function splitBufferedText(buffer: string, delimiter: string | RegExp): [string[], string] {
    const segments = buffer.split(delimiter);
    const remainder = segments.pop() ?? '';
    return [segments, remainder];
}

/**
 * Generator function to stream responses from fetch calls.
 */
async function* readStream(reader: ReadableStreamDefaultReader<string>) {
    // Attach Reader
    while (true) {
        // wait for next encoded chunk
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        // check if stream is done
        if (done) {
            break;
        }
        // Decodes data chunk and yields it
        yield value;
    }
}

/**
 * Manages a readable stream type response to intercept each chunk, format them, and return them as they are received.
 *
 * Network reads don't align with SSE event boundaries: a single event (e.g. a large `done` event
 * carrying a full answer and its sources) can arrive split across several reads on a slow
 * connection. Incomplete trailing text is buffered here until it's completed by a later read (or
 * flushed once the stream ends), instead of being parsed prematurely
 */
export async function* formatStreamResponse<Events extends StreamEvent = StreamEvent>(
    /**
     * The readable stream to format
     */
    stream: ReadableStream,
): AsyncGenerator<StreamingResponse<Events>> {
    /** Decode the stream as each chunk are encoded in binary by default  */
    const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

    /** Array to store each chunk */
    let streamResponse: StreamingResponse<Events> = {
        reader,
        lastChunk: undefined,
        chunks: [],
    };

    /** Parse a single, complete SSE event into a formatted chunk and append it to the response. */
    const appendEvent = (event: string) => {
        try {
            /** Extract the event name and data from the event */
            const eventData = event.matchAll(EXTRACT_STREAMING_FIELDS_REGEX);

            /** Convert it to an array */
            const eventDataToArray = Array.from(eventData);

            /**
             * Reduce the array of fields to an object that will group each field by name.
             * If no event name is given, it will go to a `default` key.
             */
            const lastChunk = eventDataToArray.reduce(
                (acc, [, fieldName, fieldValue]) => {
                    // Replace an `undefined` event name with the default one
                    const appendDefaultEventName = (eventName: string) => eventName ?? DEFAULT_STREAM_EVENT_NAME;
                    // Parse data and event name
                    const fieldData = fieldName === 'data' ? parseJson(fieldValue) : appendDefaultEventName(fieldValue);

                    return { ...acc, [fieldName]: fieldData };
                },
                { rawChunk: event } as StreamFormattedChunk<Events>,
            );

            const previousChunks = streamResponse.chunks || [];
            /** Append the new chunk to the rest of the stream */
            streamResponse = {
                ...streamResponse,
                lastChunk,
                chunks: [...previousChunks, lastChunk],
            };
        } catch (error) {
            logException(error);
        }
        return streamResponse;
    };

    /** Bytes received but not yet terminated by the event delimiter */
    let buffer = '';

    for await (const chunk of readStream(reader)) {
        buffer += chunk;

        // Split off every complete event and keep the last (possibly partial) segment
        // buffered until its delimiter arrives on a later read.
        const [events, remainder] = splitBufferedText(buffer, SSE_FRAME_DELIMITER);
        buffer = remainder;

        for (const event of events) {
            // Skip empty segments produced by leading/consecutive delimiters.
            if (event.trim()) {
                /** Submit the chunk to the onChunkDownload callback */
                yield appendEvent(event);
            }
        }
    }

    // Flush a trailing event that wasn't terminated by a blank line
    if (buffer.trim()) {
        yield appendEvent(buffer);
    }

    /** Once the stream ends, return the complete data as response */
    return streamResponse;
}

/** Read the given stream async generator and call the given callback when each chunk is received  */
export const readStreamGenerator = async <Events extends StreamEvent>(
    stream: AsyncGenerator<StreamingResponse<Events>>,
    onChunkDownload: (chunk: StreamingResponse<Events>) => void,
    onError?: (error: unknown) => void,
) => {
    try {
        for await (const chunks of stream) {
            onChunkDownload(chunks);
        }
    } catch (error) {
        // Catch fails (e.g. API GW closes the connection)
        onError?.(error);
    }
};
